/* eslint-disable @atlaskit/platform/use-recommended-utils */
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { getTranscriptions } from '../../api/cc-smarts/getTranscriptions';
import { createBase64FromBlob } from '../../common/createBase64';

import { applyAudioGain, calculateNormalizationGain } from './applyAudioGain';
import { hasAudioContent, hasAudioContentComprehensive } from './audioSoundDetection';
import { createPCM16FromAudioBuffer } from './createPCM16FromAudioBuffer';
import { createWAVFromAudioBuffer } from './createWAVFromAudioBuffer';
import { getLastRecorderAudioChunk } from './getLastRecorderAudioChunk';

/**
 * Processes audio chunks to text transcription
 * This function is designed to work with mono audio (single channel) as configured in getUserMedia
 * Includes sound detection to skip processing silent audio chunks
 */
export const processWEBMAudioToTextWithWAV = async (
	audioChunks: Blob[],
	lastProcessedPosition: number,
	sampleRate: number,
	audioGain: {
		autoNormalize?: boolean;
		enableGain: boolean;
		gainFactor?: number;
		preventClipping?: boolean;
		targetRMS?: number;
	} = {
		enableGain: false,
		gainFactor: 3.0,
		autoNormalize: false,
		targetRMS: 0.2,
		preventClipping: true,
	},
) => {
	const completeAudio = new Blob(audioChunks);

	const { audioBuffer, newAudioEndPosition } = await getLastRecorderAudioChunk(
		completeAudio,
		lastProcessedPosition,
		sampleRate,
	);

	if (!audioBuffer) {
		return { audioString: null, newAudioEndPosition };
	}

	// Apply audio gain if enabled
	let processedAudioBuffer = audioBuffer;
	if (audioGain.enableGain) {
		const {
			gainFactor = 1.0,
			autoNormalize = false,
			targetRMS = 0.2,
			preventClipping = true,
		} = audioGain;

		const finalGainFactor = autoNormalize
			? calculateNormalizationGain(audioBuffer, targetRMS)
			: gainFactor;

		processedAudioBuffer = applyAudioGain(audioBuffer, finalGainFactor, preventClipping);
	}

	const wavBuffer = createWAVFromAudioBuffer(processedAudioBuffer);
	const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });

	const audioString = await createBase64FromBlob(wavBlob);
	const transcription = await getTranscriptions({ audioString });

	return { transcription: transcription[0], newAudioEndPosition };
};

/**
 * Processes audio chunks to base64 string (V2)
 * This function is designed to work with mono audio (single channel) as configured in getUserMedia
 * Includes sound detection to skip processing silent audio chunks
 * Includes audio gain to normalize the audio
 */
export const processWEBMAudioToTextWithPCM16 = async (
	audioChunks: Blob[],
	lastProcessedPosition: number,
	sampleRate: number,
	soundDetection: {
		enableSoundDetection: boolean;
		soundThreshold: number;
		useComprehensiveDetection: boolean;
	} = {
		enableSoundDetection: FeatureGates.getExperimentValue(
			'ai-stt-prompt',
			'clientEnableSoundDetection',
			false,
		),
		useComprehensiveDetection: FeatureGates.getExperimentValue(
			'ai-stt-prompt',
			'clientUseComprehensiveDetection',
			false,
		),
		soundThreshold: FeatureGates.getExperimentValue('ai-stt-prompt', 'clientSoundThreshold', 0.005),
	},
	audioGain: {
		autoNormalize?: boolean;
		enableGain: boolean;
		gainFactor?: number;
		preventClipping?: boolean;
		targetRMS?: number;
	} = {
		enableGain: FeatureGates.getExperimentValue('ai-stt-prompt', 'clientEnableGain', false),
		gainFactor: FeatureGates.getExperimentValue('ai-stt-prompt', 'clientGainFactor', 3.0),
		autoNormalize: FeatureGates.getExperimentValue('ai-stt-prompt', 'clientAutoNormalize', false),
		targetRMS: FeatureGates.getExperimentValue('ai-stt-prompt', 'clientTargetRMS', 0.2),
		preventClipping: FeatureGates.getExperimentValue(
			'ai-stt-prompt',
			'clientPreventClipping',
			true,
		),
	},
) => {
	const { enableSoundDetection, useComprehensiveDetection, soundThreshold } = soundDetection;
	const completeAudio = new Blob(audioChunks);

	const { audioBuffer, newAudioEndPosition } = await getLastRecorderAudioChunk(
		completeAudio,
		lastProcessedPosition,
		sampleRate,
	);

	if (!audioBuffer) {
		return { audioString: null, newAudioEndPosition };
	}

	if (enableSoundDetection) {
		const hasSound = useComprehensiveDetection
			? hasAudioContentComprehensive(audioBuffer, { rmsThreshold: soundThreshold })
			: hasAudioContent(audioBuffer, soundThreshold);

		if (!hasSound) {
			return { audioString: null, newAudioEndPosition };
		}
	}

	let processedAudioBuffer = audioBuffer;
	if (audioGain.enableGain) {
		const {
			gainFactor = 1.0,
			autoNormalize = false,
			targetRMS = 0.2,
			preventClipping = true,
		} = audioGain;

		const finalGainFactor = autoNormalize
			? calculateNormalizationGain(audioBuffer, targetRMS)
			: gainFactor;

		processedAudioBuffer = applyAudioGain(audioBuffer, finalGainFactor, preventClipping);
	}

	const pcm16Buffer = createPCM16FromAudioBuffer(processedAudioBuffer);
	const pcm16Blob = new Blob([pcm16Buffer], { type: 'audio/pcm' });

	const audioString = await createBase64FromBlob(pcm16Blob);

	return { audioString, newAudioEndPosition };
};
